import React, { useCallback, useState } from 'react';
import LoadingButton from './common/LoadingButton';
import { Formik, Form, Field } from 'formik';

interface MailingListFieldInterface {
  label: string;
  placeholder: string;
  name: string;
}

const MailingListField = (props: MailingListFieldInterface) => {
  return (
    <div className="flex flex-col">
      <label
        htmlFor={props.name}
        className="text-foreground font-semibold mb-2 text-sm"
      >
        {props.label}
      </label>
      <Field
        className="rounded-xl p-4 border border-gray-300 bg-white text-foreground text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
        placeholder={props.placeholder}
        id={props.name}
        name={props.name}
      />
    </div>
  );
};

const MailingListSignup: React.FC = () => {
  const [submitState, setSubmitState] = useState('IDLE');
  const submitForm = useCallback(async values => {
    try {
      setSubmitState('SUBMITTING');
      const response = await fetch(
        `${process.env.GATSBY_FUNCTIONS_PATH}/mailingListSignup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: values.name,
            email: values.email
          })
        }
      );

      if (!response.ok) {
        setSubmitState('SUBMIT_ERROR');
        return;
      }

      const data = await response.json();

      if (data.Code) {
        setSubmitState('SUBMIT_ERROR');
      } else {
        // Send welcome email after successful signup
        try {
          const welcomeEmailResponse = await fetch(
            `${process.env.GATSBY_TCAL_API_URL}/mailing-list/send-welcome-email`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                name: values.name,
                email: values.email
              })
            }
          );

          // Don't fail the signup if email fails, just log it
          if (!welcomeEmailResponse.ok) {
            console.error('Failed to send welcome email');
          }
        } catch (emailErr) {
          // Don't fail the signup if email fails, just log it
          console.error('Error sending welcome email:', emailErr);
        }

        setSubmitState('SUBMIT_SUCCESS');
      }
    } catch (err) {
      setSubmitState('SUBMIT_ERROR');
    }
  }, []);

  return (
    <div className="magazine-card bg-white creative-shadow p-8 lg:p-12">
      <div className="text-center mb-8">
        <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-4">
          Join the Mailing List
        </h2>
        <p className="text-foreground/70 text-lg">
          Sign up to join our community of readers and writers
        </p>
      </div>
      <Formik
        initialValues={{
          name: '',
          email: ''
        }}
        onSubmit={values => {
          submitForm(values);
        }}
      >
        <Form className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <MailingListField label="Name" placeholder="John Doe" name="name" />
          </div>
          <div className="flex-1">
            <MailingListField
              label="Email"
              placeholder="john@doe.com"
              name="email"
            />
          </div>
          <div className="flex items-center mt-4">
            <div className="flex items-center h-full">
              <LoadingButton
                type="submit"
                text="Submit"
                size="lg"
                isLoading={submitState === 'SUBMITTING'}
              />
            </div>
          </div>
        </Form>
      </Formik>
      <div className="mt-6">
        {submitState === 'SUBMIT_SUCCESS' && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-700 text-center">
              Thanks for joining our mailing list! You've been successfully
              added.
            </p>
          </div>
        )}
        {submitState === 'SUBMIT_ERROR' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-center">
              There was a problem adding you to the mailing list. Please try
              again.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MailingListSignup;
